import {
  FormItem,
  Input,
  ArrayCollapse,
  FormCollapse,
  Select,
  NumberPicker,
} from '@formily/antd';
import { Form, registerValidateRules } from '@formily/core';
import { FormProvider, createSchemaField } from '@formily/react';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlatform } from '@/infrastructure/product-info';
import { ApiDetail, getApiDetail } from '@/presentation/hooks/compliance/api';
import { getApiInfo } from '@/presentation/pages/compliance/utils';

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    ArrayCollapse,
    FormCollapse,
    Select,
    NumberPicker,
  },
});

const SupportCacheTimeRules = [
  'storage_storage',
  'cache_cache',
  'cache_normal',
  'ban_cache',
  'cacheOnly_cache',
];

interface IProps {
  initContent: any;
  form: Form;
  readOnly?: boolean;
}

interface ApiRule {
  module: string;
  apis?: string[];
  pages?: string[]
  rule?: string;
  highFreq?: string;
}

interface SceneRate {
  scene: string;
  rate: number;
  maxReport: number;
}

interface FormContent {
  rules?: ApiRule[];
  sceneSampleRate?: SceneRate[];
}

interface ConfigContent {
  v1?: {
    rules?: ApiRule[];
    sample?: {
      sceneSampleRate?: SceneRate[];
      totalSampleRate?: number;
      totalMaxReport?: number;
    }
  }
}

export const ComplianceEditor: FC<IProps> = ({ initContent, form, readOnly }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<ApiDetail>();
  const {
    infos = [],
    tips = {},
    rates = [],
    highFreqRules = [],
    silenceRules = [],
    cacheTimeRules = [],
  } = detail || {};
  const modules = Array.from(new Set(infos.map(item => item.module)));
  const moduleEnum = modules.map(module => ({ value: module }));
  const loadApiEnum = (module: string) => {
    const filterInfos = infos.filter(item => item.module === module);
    return filterInfos.map(item => ({
      value: item.shortName || item.api,
      label: item.api,
    }));
  };
  const loadRuleEnum = (module: string, apis: string[]) => {
    const filterRules = infos
      .filter((item) => {
        if (apis.length > 0) {
          // 如果有apis, 则按apis筛选
          return apis.includes(item.shortName || item.api || '');
        }
        // 否则按module筛选
        return item.module === module;
      })
      .map(item => item.supportRules);

    // 找并集
    const fixedRules = filterRules.reduce((pre, cur, index) => {
      if (index === 0) {
        return cur;
      }
      return pre?.filter(item => cur?.includes(item));
    }, []);

    return fixedRules?.map(rule => ({
      label: tips[rule],
      value: rule,
    }));
  };
  const loadHighFreqEnum = (module: string, apis: string[]) => {
    if (module || apis.length > 0) {
      return highFreqRules.map(highFreq => ({
        label: highFreq.description,
        value: highFreq.freq,
      }));
    }
    return [];
  };
  const loadSilenceEnum = (module: string, apis: string[]) => {
    if (module || apis.length > 0) {
      return silenceRules.map(silence => ({
        label: silence.description,
        value: silence.time,
      }));
    }
    return [];
  };
  const loadCacheTimeEnum = (rule?: string) => {
    if (rule && SupportCacheTimeRules.includes(rule)) {
      return cacheTimeRules.map(cacheTime => ({
        label: cacheTime.description,
        value: cacheTime.time,
      }));
    }
    return [];
  };
  const sceneEnum = rates.map(item => ({
    value: item.scene,
    label: tips[item.scene || ''],
  }));

  const loadApiInfos = async () => {
    const apiDetail = await getApiDetail({ platform: getPlatform() });
    setDetail(apiDetail);
  };

  const loadRateSceneLabel = (rateScene: string) => tips[rateScene];

  const validateRules = (rules: ApiRule[]) => {
    const errors: string[] = [];
    const map = new Map<string, ApiRule>();
    const validate = function (key: string, validateRuleEntity: ApiRule) {
      if (map.get(key)) {
        errors.push(t('重复设置了通用规则: {{key}}', { key }));
      } else {
        map.set(key, validateRuleEntity);
      }
    };
    rules.forEach((ruleEntity) => {
      const { module, apis, pages } = ruleEntity;
      if (apis && apis.length > 0) {
        apis.forEach((api) => {
          if (pages && pages.length > 0) {
            pages.forEach((page) => {
              const key = `${module}-${getApiInfo(infos, undefined, api)?.api}-${page}`;
              validate(key, ruleEntity);
            });
          } else {
            const key = `${module}-${getApiInfo(infos, undefined, api)?.api}`;
            validate(key, ruleEntity);
          }
        });
      } else {
        const key = `${module}`;
        validate(key, ruleEntity);
      }
    });
    if (errors.length > 0) {
      return errors[0];
    }

    return '';
  };

  const validateRates = (rates: SceneRate[]) => {
    const errors: string[] = [];
    const map = new Map<string, SceneRate>();
    rates.forEach((rateEntity) => {
      const { scene } = rateEntity;
      if (map.has(scene)) {
        errors.push(t('重复设置了场景采样率: {{scene}}', { scene: loadRateSceneLabel(scene) }));
      } else {
        map.set(scene, rateEntity);
      }
    });
    if (errors.length > 0) {
      return errors[0];
    }
    return '';
  };

  useEffect(() => {
    // 加载API规则
    loadApiInfos();
  }, []);

  useEffect(() => {
    // 设置校验规则
    registerValidateRules({
      validateRules,
      validateRates,
    });
  });

  useEffect(() => {
    form.setInitialValues(transferFromConfig(initContent));
  }, [form]);

  const schema = {
    type: 'object',
    properties: {
      collapse: {
        type: 'void',
        'x-decorator': 'FormItem',
        'x-component': 'FormCollapse',
        properties: {
          panel1: {
            type: 'void',
            'x-component': 'FormCollapse.CollapsePanel',
            'x-component-props': {
              header: t('API通用规则'),
            },
            properties: {
              rules: {
                type: 'array',
                'x-component': 'ArrayCollapse',
                'x-decorator': 'FormItem',
                items: {
                  type: 'void',
                  'x-component': 'ArrayCollapse.CollapsePanel',
                  'x-component-props': {
                    header: t('通用规则'),
                  },
                  properties: {
                    index: {
                      type: 'void',
                      'x-component': 'ArrayCollapse.Index',
                    },
                    collapse: {
                      type: 'object',
                      'x-decorator': 'FormItem',
                      properties: {
                        module: {
                          type: 'string',
                          title: 'module',
                          enum: moduleEnum,
                          'x-component': 'Select',
                          'x-component-props': {
                            disabled: readOnly,
                            placeholder: t('请选择module'),
                          },
                          'x-decorator': 'FormItem',
                          required: true,
                        },
                        apis: {
                          type: 'array',
                          title: 'apis',
                          'x-component': 'Select',
                          'x-component-props': {
                            multiple: true,
                            mode: 'multiple',
                            disabled: readOnly,
                            placeholder: t('请选择apis, 值为空时代表全选'),
                          },
                          'x-decorator': 'FormItem',
                          'x-reactions': {
                            dependencies: ['.module'],
                            fulfill: {
                              state: {
                                dataSource: '{{loadApiEnum($deps[0])}}',
                                value: [],
                              },
                            },
                          },
                        },
                        pages: {
                          type: 'array',
                          title: 'pages',
                          'x-component': 'Select',
                          'x-component-props': {
                            multiple: true,
                            mode: 'tags',
                            disabled: readOnly,
                            open: false,
                            placeholder: t('请选择pages, 值为空时代表全选'),
                          },
                          'x-decorator': 'FormItem',
                          'x-reactions': {
                            dependencies: ['.module'],
                            fulfill: {
                              state: {
                                value: [],
                              },
                            },
                          },
                        },
                        rule: {
                          type: 'string',
                          title: 'rule',
                          required: true,
                          'x-component': 'Select',
                          'x-component-props': {
                            disabled: readOnly,
                          },
                          'x-decorator': 'FormItem',
                          'x-reactions': {
                            dependencies: ['.module', '.apis'],
                            fulfill: {
                              state: {
                                dataSource: '{{loadRuleEnum($deps[0], $deps[1])}}',
                                value: '',
                              },
                            },
                          },
                        },
                        highFreq: {
                          type: 'string',
                          title: 'highFreq',
                          'x-component': 'Select',
                          'x-component-props': {
                            disabled: readOnly,
                          },
                          'x-decorator': 'FormItem',
                          'x-reactions': {
                            dependencies: ['.module', '.apis'],
                            fulfill: {
                              state: {
                                dataSource: '{{loadHighFreqEnum($deps[0], $deps[1])}}',
                                value: '',
                              },
                            },
                          },
                        },
                        silence: {
                          type: 'string',
                          title: 'silence',
                          'x-component': 'Select',
                          'x-component-props': {
                            disabled: readOnly,
                          },
                          'x-decorator': 'FormItem',
                          'x-reactions': {
                            dependencies: ['.module', '.apis'],
                            fulfill: {
                              state: {
                                dataSource: '{{loadSilenceEnum($deps[0], $deps[1])}}',
                                value: '',
                              },
                            },
                          },
                        },
                        cacheTime: {
                          type: 'string',
                          title: 'cacheTime',
                          'x-component': 'Select',
                          'x-component-props': {
                            disabled: readOnly,
                          },
                          'x-decorator': 'FormItem',
                          'x-reactions': {
                            dependencies: ['.rule'],
                            fulfill: {
                              state: {
                                dataSource: '{{loadCacheTimeEnum($deps[0])}}',
                                value: '',
                              },
                            },
                          },
                        },
                      },
                    },
                    remove: readOnly ? {} : {
                      type: 'void',
                      'x-component': 'ArrayCollapse.Remove',
                      'x-component-props': {
                        disabled: true,
                      },
                    },
                  },
                },
                properties: {
                  addition: readOnly ? {} : {
                    type: 'void',
                    title: t('增加通用规则'),
                    'x-component': 'ArrayCollapse.Addition',
                  },
                },
                'x-validator': {
                  validateRules: true,
                },
              },
            },
          },
          panel2: {
            type: 'void',
            'x-component': 'FormCollapse.CollapsePanel',
            'x-component-props': {
              header: t('采样率配置'),
            },
            properties: {
              sceneSampleRate: {
                type: 'array',
                'x-component': 'ArrayCollapse',
                'x-decorator': 'FormItem',
                items: {
                  type: 'void',
                  'x-component': 'ArrayCollapse.CollapsePanel',
                  'x-component-props': {
                    header: t('场景采样率'),
                  },
                  properties: {
                    index: {
                      type: 'void',
                      'x-component': 'ArrayCollapse.Index',
                    },
                    collapse: {
                      type: 'object',
                      properties: {
                        scene: {
                          type: 'array',
                          title: 'scene',
                          'x-component': 'Select',
                          'x-decorator': 'FormItem',
                          enum: sceneEnum,
                          required: true,
                          readOnly,
                        },
                        rate: {
                          type: 'number',
                          title: 'rate',
                          'x-component': 'NumberPicker',
                          'x-decorator': 'FormItem',
                          required: true,
                          readOnly,
                          'x-validator': {
                            maximum: 1,
                            exclusiveMinimum: 0,
                          },
                        },
                        maxReport: {
                          type: 'number',
                          title: 'maxReport',
                          'x-component': 'NumberPicker',
                          'x-decorator': 'FormItem',
                          required: true,
                          readOnly,
                          'x-validator': {
                            minimum: 0,
                          },
                        },
                      },
                    },
                    remove: readOnly ? {} : {
                      type: 'void',
                      'x-component': 'ArrayCollapse.Remove',
                    },
                  },
                },
                properties: {
                  addition: readOnly ? {} : {
                    type: 'void',
                    title: t('增加场景采样率'),
                    'x-component': 'ArrayCollapse.Addition',
                  },
                },
                'x-validator': {
                  validateRates: true,
                },
              },
            },
          },
        },
      },
    },
  };

  return (
    detail ? <FormProvider form={form}>
      <SchemaField
        schema={schema}
        scope={{ loadApiEnum, loadRuleEnum, loadHighFreqEnum, loadSilenceEnum, loadCacheTimeEnum }}
        editable={!readOnly}
      />
    </FormProvider> : null
  );
};

export const transferToConfig = (content: FormContent): ConfigContent => {
  // 从表单数据转成配置
  const { rules = [], sceneSampleRate = [] } = content;
  const globalRate = sceneSampleRate.find(({ scene }) => scene === 'global');
  const fixedSceneSampleRate = sceneSampleRate.filter(({ scene }) => scene !== 'global');

  return {
    v1: {
      rules,
      sample: {
        sceneSampleRate: fixedSceneSampleRate,
        totalSampleRate: globalRate?.rate,
        totalMaxReport: globalRate?.maxReport,
      },
    },
  };
};

export const transferFromConfig = (content: ConfigContent): FormContent => {
  // 从配置转换成表单数据
  const { rules, sample } = content.v1 || {};
  const sceneSampleRate = sample?.sceneSampleRate || [];
  if (sample?.totalSampleRate && sample?.totalMaxReport) {
    sceneSampleRate.push({
      scene: 'global',
      rate: sample.totalSampleRate,
      maxReport: sample.totalMaxReport,
    });
  }

  return {
    rules,
    sceneSampleRate,
  };
};
